/**
 * Visa Service
 * 
 * Service layer for visa application API calls
 */

import { apiClient } from './api-client';

/**
 * Type definitions matching the backend schemas
 */
export interface ImageUploadRequest {
  image_data: string;  // Base64
  image_type: 'passport' | 'photograph';
}

export interface ImageUploadResponse {
  url: string;  // S3  URL
  image_type: string;
}

export interface VisaApplicationData {
  // Note: Step 1 fields (full_name, phone_number, email, visa_type) 
  // are already saved in the draft and not needed here
  
  // Step 2: Documents
  passport_image: string;  // S3 URL
  photograph_image: string;  // S3 URL
  
  // Step 3: Travel Details
  port_of_arrival: string;
  expected_arrival_date: string;  // YYYY-MM-DD format
  places_to_visit: string;
  
  // Step 4: Personal & Family Details
  father_full_name: string;
  father_place_of_birth: string;
  mother_full_name: string;
  mother_place_of_birth: string;
  marital_status: 'single' | 'married' | 'other';
  spouse_full_name?: string;
  spouse_nationality?: string;
  spouse_place_of_birth?: string;
  
  // Step 5: Address, Education & Religion
  residential_address: string;
  educational_qualifications: string;
  religion: string;
  
  // Step 6: Travel History
  visited_india_before: boolean;
  cities_visited_in_india?: string;
  visited_saarc_countries: boolean;
  history_of_overstaying: boolean;
  overstay_explanation?: string;
  previous_visa_rejections: boolean;
  visa_rejection_explanation?: string;
  refused_entry_any_country: boolean;
  refused_entry_explanation?: string;
  arrested_convicted: boolean;
  trafficking_crimes: boolean;
  cyber_terror_violence: boolean;
  glorify_terrorism: boolean;
  sought_asylum: boolean;
  
  // Step 7: Declaration
  declaration_accepted: boolean;
}

export type ApplicationStatus =
  | 'draft'
  | 'step2_completed'
  | 'step3_completed'
  | 'step4_completed'
  | 'step5_completed'
  | 'step6_completed'
  | 'step7_completed'
  | 'submitted'
  | 'payment_pending'
  | 'payment_confirmed'
  | 'processing'
  | 'embassy_submitted'
  | 'approved'
  | 'rejected';

export interface VisaApplicationResponse {
  id: string;
  application_number: string;
  status: ApplicationStatus;
  created_at: string;
  full_name: string;
  phone_number: string;
  email: string;
  visa_type: string;
  port_of_arrival: string | null;
  expected_arrival_date: string | null;
}

/** Full application detail (for resume / GET application) */
export interface VisaApplicationDetail {
  id: string;
  application_number: string;
  status: ApplicationStatus;
  created_at: string;
  updated_at: string;
  full_name: string;
  phone_number: string;
  email: string;
  visa_type: string;
  port_of_arrival: string | null;
  expected_arrival_date: string | null;
  places_to_visit: string | null;
  father_full_name: string | null;
  father_place_of_birth: string | null;
  mother_full_name: string | null;
  mother_place_of_birth: string | null;
  marital_status: string | null;
  spouse_full_name: string | null;
  spouse_nationality: string | null;
  spouse_place_of_birth: string | null;
  residential_address: string | null;
  educational_qualifications: string | null;
  religion: string | null;
  educational_qualification_institute?: string | null;
  visited_india_before: boolean | null;
  cities_visited_in_india: string | null;
  previous_visa_id: string | null;
  visited_saarc_countries: boolean | null;
  history_of_overstaying: boolean | null;
  overstay_explanation: string | null;
  previous_visa_rejections: boolean | null;
  visa_rejection_explanation: string | null;
  refused_entry_any_country: boolean | null;
  refused_entry_explanation: string | null;
  arrested_convicted: boolean | null;
  trafficking_crimes: boolean | null;
  cyber_terror_violence: boolean | null;
  glorify_terrorism: boolean | null;
  sought_asylum: boolean | null;
  payment_method: string | null;
  passport_image?: string | null;
  photograph_image?: string | null;
  declaration_accepted?: boolean;
  business_travel_purpose?: string | null;
  ebusiness_documents?: Record<string, string> | null;
}

export interface VisaApplicationStatus {
  application_number: string;
  status: ApplicationStatus;
  full_name: string;
  email: string;
  created_at: string;
 // expected_arrival_date: string | null;
}

export interface PaymentUpdateData {
  payment_method: 'bank' | 'cash' | 'card';
}

export interface CreatePaymentIntentResponse {
  client_secret: string;
  amount: number;
  currency: string;
  stripe_publishable_key: string;
}

export interface VisaApplicationDraftData {
  // Step 1: Basic Information only
  full_name: string;
  phone_number: string;
  email: string;
  visa_type: '30_days' | '1_year' | 'ebusiness_1_year';
  /** eBusiness only: purpose slug e.g. SET_UP_BUSINESS_VENTURE, TO_JOIN_THE_VESSEL */
  business_travel_purpose?: string;
}

/** Payload for saveStep(applicationNumber, step, data). Only fields for the given step are sent. */
export interface VisaApplicationStepData {
  passport_image?: string;
  photograph_image?: string;
  port_of_arrival?: string;
  expected_arrival_date?: string;
  places_to_visit?: string;
  father_full_name?: string;
  father_place_of_birth?: string;
  mother_full_name?: string;
  mother_place_of_birth?: string;
  marital_status?: 'single' | 'married' | 'other';
  spouse_full_name?: string;
  spouse_nationality?: string;
  spouse_place_of_birth?: string;
  residential_address?: string;
  educational_qualifications?: string;
  religion?: string;
  educational_qualification_institute?: string;
  visited_india_before?: boolean;
  cities_visited_in_india?: string;
  previous_visa_id?: string;
  visited_saarc_countries?: boolean;
  history_of_overstaying?: boolean;
  overstay_explanation?: string;
  previous_visa_rejections?: boolean;
  visa_rejection_explanation?: string;
  refused_entry_any_country?: boolean;
  refused_entry_explanation?: string;
  arrested_convicted?: boolean;
  trafficking_crimes?: boolean;
  cyber_terror_violence?: boolean;
  glorify_terrorism?: boolean;
  sought_asylum?: boolean;
  declaration_accepted?: boolean;
}

/**
 * Visa Service API Methods
 */
export const visaService = {
  /**
   * Upload image to S3
   */
  uploadImage: async (
    imageData: string,
    imageType: 'passport' | 'photograph',
    applicationNumber?: string
  ): Promise<ImageUploadResponse> => {
    return apiClient.post<ImageUploadResponse>(
      '/visa/upload-image',
      {
        image_data: imageData,
        image_type: imageType,
        application_number: applicationNumber
      }
    );
  },

  /**
   * Upload eBusiness supporting PDF via multipart/form-data. Saves with document type as name (e.g. BUSINESS_INVITATION.pdf).
   */
  uploadEbusinessDocument: async (
    applicationNumber: string,
    documentType: string,
    file: File
  ): Promise<{ url: string; document_type: string }> => {
    const formData = new FormData();
    formData.append('application_number', applicationNumber);
    formData.append('document_type', documentType);
    formData.append('file', file);
    return apiClient.postForm<{ url: string; document_type: string }>(
      '/visa/upload-ebusiness-document',
      formData
    );
  },

  /**
   * Create a draft visa application (Step 1 only)
   */
  createDraftApplication: async (
    data: VisaApplicationDraftData
  ): Promise<VisaApplicationResponse> => {
    return apiClient.post<VisaApplicationResponse>(
      '/visa/applications/draft',
      data
    );
  },

  /**
   * Save a single step (2-8) and update status to step{N}_completed or 'submitted' for step 8.
   */
  saveStep: async (
    applicationNumber: string,
    step: number,
    data: VisaApplicationStepData
  ): Promise<VisaApplicationResponse> => {
    return apiClient.patch<VisaApplicationResponse>(
      `/visa/applications/${applicationNumber}/step`,
      { step, ...data }
    );
  },

  /**
   * Update an existing draft application with complete information (legacy)
   */
  updateApplication: async (
    applicationNumber: string,
    data: VisaApplicationData
  ): Promise<VisaApplicationResponse> => {
    return apiClient.patch<VisaApplicationResponse>(
      `/visa/applications/${applicationNumber}`,
      data
    );
  },

  /**
   * Get full application details by application number (for resume flow)
   */
  getApplication: async (
    applicationNumber: string
  ): Promise<VisaApplicationDetail> => {
    return apiClient.get<VisaApplicationDetail>(
      `/visa/applications/${applicationNumber}`
    );
  },

  /**
   * Check application status by application number
   */
  checkStatus: async (
    applicationNumber: string
  ): Promise<VisaApplicationStatus> => {
    return apiClient.get<VisaApplicationStatus>(
      `/visa/applications/${applicationNumber}/status`
    );
  },

  /**
   * Update payment information
   */
  updatePayment: async (
    applicationNumber: string,
    data: PaymentUpdateData
  ): Promise<VisaApplicationResponse> => {
    return apiClient.patch<VisaApplicationResponse>(
      `/visa/applications/${applicationNumber}/payment`,
      data
    );
  },

  /**
   * Create Stripe PaymentIntent for card payment. Returns client_secret for Stripe.js confirm.
   */
  createPaymentIntent: async (
    applicationNumber: string
  ): Promise<CreatePaymentIntentResponse> => {
    return apiClient.post<CreatePaymentIntentResponse>(
      `/visa/applications/${applicationNumber}/create-payment-intent`,
      {}
    );
  },

  /**
   * List all applications (admin function)
   */
  listApplications: async (
    status?: string,
    limit?: number
  ): Promise<VisaApplicationResponse[]> => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (limit) params.append('limit', limit.toString());
    
    const queryString = params.toString();
    const endpoint = queryString 
      ? `/visa/applications?${queryString}` 
      : '/visa/applications';
    
    return apiClient.get<VisaApplicationResponse[]>(endpoint);
  },
};

/**
 * Helper function to format date for API
 */
export const formatDateForAPI = (date: string): string => {
  // Ensure date is in YYYY-MM-DD format
  return date;
};

/**
 * Helper function to convert file to base64
 */
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Helper function to validate base64 image
 */
export const isValidBase64Image = (base64: string): boolean => {
  return base64.startsWith('data:image/');
};
